import {ref, computed} from 'vue'

export const useCommissionCalculation = (formData: any, excludedAmenityPriceSum?: any) => {
    // Raw daily rate fetched from the API. The earnings are derived from this plus the
    // current excluded-amenity sum so the value updates live as the operator toggles
    // amenity checkboxes without re-hitting the rates endpoint.
    const dailyRate = ref<number | null>(null)

    // Commission percentage applied by the platform per sustainability level.
    const commissionPercentage = computed(() => {
        const sustainabilityLevel = formData.value?.sustainabilityLevel
        switch (sustainabilityLevel) {
            case 'BASIC':
                return 15
            case 'ZERO':
                return 10
            case 'SUSTAINABLE':
                return 5
            default:
                return 15 // Default to BASIC rate
        }
    })

    // Earnings formula (per QA): (basicPrice - sumExcludedAmenityPrices) * (1 - commission/100).
    // For BASIC rooms the excluded sum is 0, so this collapses to basicPrice * 0.85.
    // For ZERO/SUSTAINABLE the operator excludes basic-room amenities to lower the cost,
    // and the resulting adjusted price is what the commission applies to.
    const calculatedEarnings = computed(() => {
        if (dailyRate.value === null) return null
        const excludedRaw = excludedAmenityPriceSum && 'value' in excludedAmenityPriceSum
            ? excludedAmenityPriceSum.value
            : excludedAmenityPriceSum
        const excluded = Number(excludedRaw) || 0
        const adjusted = dailyRate.value - excluded
        if (adjusted <= 0) return 0
        return adjusted * (1 - commissionPercentage.value / 100)
    })

    const calculateEarnings = async () => {
        const app = useNuxtApp()
        const supplierStore = useSupplierStore()

        try {
            // Calculate date +5 days from today
            const targetDate = new Date()
            targetDate.setDate(targetDate.getDate() + 5)
            const targetDateString = targetDate.toISOString().split('T')[0] // YYYY-MM-DD format

            // ZERO/SUSTAINABLE variants don't carry their own daily rates — pricing is only
            // configured against the BASIC sibling. Use the BASIC room's id for the lookup
            // (and its name for the name-match fallback) so earnings still resolve.
            const isVariant =
                formData.value?.sustainabilityLevel === 'ZERO' ||
                formData.value?.sustainabilityLevel === 'SUSTAINABLE'
            const lookupRoomId =
                isVariant && formData.value?.basicRoomId ? formData.value.basicRoomId : formData.value?.id
            const basicRoomType =
                isVariant && formData.value?.basicRoomId
                    ? (supplierStore.roomTypes || []).find(rt => rt.id === formData.value.basicRoomId)
                    : null
            const lookupRoomName = basicRoomType?.name || formData.value?.name

            console.log(
                'Commission block: Calculating earnings for room ID:',
                lookupRoomId,
                'on date:',
                targetDateString
            )

            // Try to get daily rate for the room
            if (lookupRoomId && supplierStore.fullContract?.id) {
                const rateRequest = {
                    contractId: supplierStore.fullContract.id,
                    dateFrom: targetDateString,
                    dateTo: targetDateString,
                }

                console.log('Commission block: Making API request with:', rateRequest)

                // Get daily rates using the correct API structure
                const rateResponse = await app.$api.dailyRates.get(rateRequest)

                console.log('Commission block: API response:', rateResponse)

                if (
                    rateResponse &&
                    rateResponse.rates &&
                    rateResponse.rates.dailyRates &&
                    rateResponse.rates.dailyRates.length > 0
                ) {
                    console.log('Commission block: Found daily rates:', rateResponse.rates.dailyRates)
                    console.log('Commission block: Looking for room ID:', lookupRoomId)
                    console.log(
                        'Commission block: Available serviceIds:',
                        rateResponse.rates.dailyRates.map(r => r.serviceId)
                    )

                    // Pick the highest-amount rate among those matching the predicate.
                    // The same (room, date) can have multiple tariffs; earnings should reflect
                    // the most expensive offered price, not whichever tariff happens to come first.
                    const pickHighestRate = (predicate: (rate: any) => boolean) =>
                        rateResponse.rates.dailyRates
                            .filter(predicate)
                            .reduce(
                                (best: any, rate: any) => (best && best.amount >= rate.amount ? best : rate),
                                null
                            )

                    // Find the daily rate for our specific room (serviceId = room ID) and target date
                    let roomRate = pickHighestRate(
                        rate =>
                            rate.serviceId === lookupRoomId &&
                            rate.date === targetDateString &&
                            rate.active === true &&
                            rate.amount > 0
                    )

                    // If no exact match found, try to find a rate by exact room name match
                    if (!roomRate && lookupRoomName) {
                        console.log('Commission block: No exact room match, looking for room name:', lookupRoomName)

                        // Get the room name in the current locale. service.name may itself be a
                        // localized object on some endpoints, so resolve both sides the same way.
                        const currentLocale = app.$i18n.locale.value
                        const resolveLocalizedName = (name: any): string => {
                            if (!name) return ''
                            if (typeof name === 'string') return name
                            return name[currentLocale] || name['en'] || Object.values(name)[0] || ''
                        }
                        const roomName = resolveLocalizedName(lookupRoomName)

                        console.log('Commission block: Room name to match:', roomName)

                        // Multiple services can share the same name (e.g. basic + sustainability variants
                        // of the same room type), so collect every matching service id and pick the max
                        // rate across all of them rather than the first match.
                        const matchingServiceIds = new Set(
                            (rateResponse.rates.services || [])
                                .filter(
                                    service =>
                                        resolveLocalizedName(service.name).toLowerCase() === roomName.toLowerCase()
                                )
                                .map(service => service.id)
                        )

                        if (matchingServiceIds.size > 0) {
                            console.log('Commission block: Found matching service ids by name:', [
                                ...matchingServiceIds,
                            ])
                            roomRate = pickHighestRate(
                                rate =>
                                    matchingServiceIds.has(rate.serviceId) &&
                                    rate.date === targetDateString &&
                                    rate.active === true &&
                                    rate.amount > 0
                            )
                        } else {
                            console.log('Commission block: No service found with name:', roomName)
                        }
                    }

                    console.log('Commission block: Final selected room rate:', roomRate)

                    if (roomRate && roomRate.amount) {
                        dailyRate.value = roomRate.amount
                        console.log('Commission block: Daily rate resolved:', dailyRate.value)
                    } else {
                        console.log('Commission block: No valid room rate found')
                        dailyRate.value = null
                    }
                } else {
                    console.log('Commission block: No daily rates found in response')
                    dailyRate.value = null
                }
            } else {
                console.log('Commission block: Missing room ID or contract ID')
                dailyRate.value = null
            }
        } catch (error) {
            console.log('Commission block: Error fetching daily rates:', error)
            dailyRate.value = null
        }
    }

    return {
        calculatedEarnings,
        commissionPercentage,
        calculateEarnings,
    }
}
